import { getAll, getOne, postWithResponse } from '../CommonServices/httpService'
import { ForemanCache, WorkItemAttachmentCache } from '../../types/Cache/ForemanCache'
import { ForemanDB } from '../../types/IndexedDB/ForemanDB'
import { WorkArea } from '../../types/Project/WorkArea'
import { useTokenClaims } from '../../security/useTokenClaims'
import { MutationSnapshot } from '../../types/Cache/MutationSnapshot'
import { MutationSnapshotService } from '../CacheFallbackServices/mutationSnapshotService'
import dayjs, { Dayjs } from 'dayjs'
import { AttachmentsUploadResponse } from '../../types/Upload/AttachmentResponse'
import { processAttachmentAnnotations } from '../attachmentAnnotationService'
import { OfflineMutation } from '../../types/Core/OfflineMutation'

const fetchAttachmentBlobData = async (attachments: WorkItemAttachmentCache[]) => {
	const blobData: Array<{ id: number; blob: Blob }> = []

	for (const attachment of attachments) {
		try {
			const url = `${attachment.uri}${attachment.sasToken ?? ''}`
			const res = await fetch(url)
			if (!res.ok) {
				console.warn(`HTTP ${res.status} for attachment ${attachment.id}: ${url}`)
				continue
			}

			if (res.type === 'opaque') {
				console.warn(`Opaque response (CORS) for attachment ${attachment.id}, skipping`)
				continue
			}

			const buf = await res.arrayBuffer()
			const type = res.headers.get('content-type') ?? 'application/pdf'
			const blob = new Blob([buf], { type })

			blobData.push({ id: attachment.id, blob })
		} catch (err: any) {
			console.error(`Error fetching attachment blob ${attachment.id}:`, err)
		}
	}

	return blobData
}

export const cacheForemanData = async (db: ForemanDB) => {
	const { projectId } = useTokenClaims()
	await db.setSyncInProgress(true)
	try {
		const [foremanData, workAreas, consent] = await Promise.all([
			getOne<ForemanCache>('/Offline/Snapshot'),
			//Intercepted by service worker
			getAll<WorkArea[]>(`/Projects/${projectId}/Areas`, 'Project Areas failed to load'),
			getOne<{ dayStartPrompt: string; dayEndPrompt: string }>(`/Projects/Consent`),
		])
		// Fetch all attachment blob data BEFORE opening any database transactions
		let attachmentBlobData: Array<{ id: number; blob: Blob }> = []
		if (foremanData.data.attachments && foremanData.data.attachments.length > 0) {
			const attachmentsToUpdate: WorkItemAttachmentCache[] = []
			await db.open()
			const existingAttachments = await db.workItemAttachments.toArray()

			for (const a of foremanData.data.attachments) {
				const attachmentInCache = existingAttachments.find((existing) => existing.id === a.id)
				if (!attachmentInCache) {
					attachmentsToUpdate.push(a)
				} else if (dayjs(a.updatedAt).isAfter(dayjs(attachmentInCache.updatedAt))) {
					attachmentsToUpdate.push(a)
				}
			}

			if (attachmentsToUpdate.length > 0) {
				attachmentBlobData = await fetchAttachmentBlobData(attachmentsToUpdate)
			}
		}

		await db.open()

		await db.transaction(
			'rw',
			[
				db.cacheInfo,
				db.employee,
				db.directReports,
				db.workItems,
				db.tasks,
				db.reworks,
				db.constraints,
				db.requests,
				db.workItemAssignments,
				db.taskAssignments,
				db.reworkAssignments,
				db.qcWorkers,
				db.attendance,
				db.pallets,
				db.palletItems,
				db.timesheets,
				db.timeRecords,
				db.workItemAttachments,
				db.workItemAttachmentBlobs,
				db.mutationSnapshot,
			],
			async () => {
				// disable hooks
				db.setHooksEnabled(false)
				// Clear existing cache
				await db.cacheInfo.clear()
				await db.employee.clear()
				await db.directReports.clear()
				await db.workItems.clear()
				await db.tasks.clear()
				await db.reworks.clear()
				await db.constraints.clear()
				await db.requests.clear()
				await db.workItemAssignments.clear()
				await db.taskAssignments.clear()
				await db.reworkAssignments.clear()
				await db.qcWorkers.clear()
				await db.attendance.clear()
				await db.pallets.clear()
				await db.palletItems.clear()
				await db.timesheets.clear()
				await db.timeRecords.clear()
				// Store cache info
				await db.cacheInfo.add({
					userId: foremanData.data.employee.id,
					createdAt: foremanData.data.createdAt,
				})

				// Store employee data
				if (foremanData.data.employee) {
					await db.employee.add(foremanData.data.employee)
				}

				// Store direct reports
				if (foremanData.data.directReports && foremanData.data.directReports.length > 0) {
					await db.directReports.bulkAdd(foremanData.data.directReports)
				}

				// Store work items
				if (foremanData.data.workItems && foremanData.data.workItems.length > 0) {
					await db.workItems.bulkAdd(foremanData.data.workItems)
				}

				// Store tasks
				if (foremanData.data.tasks && foremanData.data.tasks.length > 0) {
					await db.tasks.bulkAdd(foremanData.data.tasks)
				}

				// Store reworks
				if (foremanData.data.reworks && foremanData.data.reworks.length > 0) {
					await db.reworks.bulkAdd(foremanData.data.reworks)
				}

				// Store constraints
				if (foremanData.data.constraints && foremanData.data.constraints.length > 0) {
					await db.constraints.bulkAdd(foremanData.data.constraints)
				}

				// Store requests
				if (foremanData.data.requests && foremanData.data.requests.length > 0) {
					await db.requests.bulkAdd(foremanData.data.requests)
				}

				// Store work item assignments
				if (
					foremanData.data.workItemAssignments &&
					foremanData.data.workItemAssignments.length > 0
				) {
					await db.workItemAssignments.bulkAdd(foremanData.data.workItemAssignments)
				}

				// Store task assignments
				if (foremanData.data.taskAssignments && foremanData.data.taskAssignments.length > 0) {
					await db.taskAssignments.bulkAdd(foremanData.data.taskAssignments)
				}

				// Store rework assignments
				if (foremanData.data.reworkAssignments && foremanData.data.reworkAssignments.length > 0) {
					await db.reworkAssignments.bulkAdd(foremanData.data.reworkAssignments)
				}

				// Store QC workers
				if (foremanData.data.qcWorkers && foremanData.data.qcWorkers.length > 0) {
					await db.qcWorkers.bulkAdd(foremanData.data.qcWorkers)
				}
				// Store attendance
				if (foremanData.data.attendance && foremanData.data.attendance.length > 0) {
					await db.attendance.bulkAdd(foremanData.data.attendance)
				}
				// Store pallets
				if (foremanData.data.pallets && foremanData.data.pallets.length > 0) {
					await db.pallets.bulkAdd(foremanData.data.pallets)
				}
				// Store pallet items
				if (foremanData.data.palletItems && foremanData.data.palletItems.length > 0) {
					await db.palletItems.bulkAdd(foremanData.data.palletItems)
				}
				// Store timesheets
				if (foremanData.data.timesheets && foremanData.data.timesheets.length > 0) {
					await db.timesheets.bulkAdd(foremanData.data.timesheets)
				}
				// Store time records
				if (foremanData.data.timeRecords && foremanData.data.timeRecords.length > 0) {
					await db.timeRecords.bulkAdd(foremanData.data.timeRecords)
				}
				// Update attachments
				if (foremanData.data.attachments && foremanData.data.attachments.length > 0) {
					const newAttachmentIds = foremanData.data.attachments.map((a) => a.id)
					await db.workItemAttachments.where('id').noneOf(newAttachmentIds).delete()
					await db.workItemAttachmentBlobs.where('id').noneOf(newAttachmentIds).delete()
					await db.workItemAttachments.bulkPut(foremanData.data.attachments)
					if (attachmentBlobData.length > 0) {
						await db.workItemAttachmentBlobs.bulkPut(attachmentBlobData)
					}
				}
				// enable hooks
				db.setHooksEnabled(true)
				const msService = new MutationSnapshotService(db)
				await msService.initializeSnapshot(foremanData.data.employee.id)
			},
		)
	} finally {
		await db.setSyncInProgress(false)
	}
}

export const getAvailableTimesheetDate = async (db: ForemanDB) => {
	const timesheet = await db.timesheets.orderBy('id').first()
	return timesheet?.localDate ?? null
}

export const checkTimesheetDate = async (db: ForemanDB, date: Dayjs, connectionStatus: string) => {
	if (connectionStatus === 'online') {
		return true
	}
	const timesheetsInCache = await db.timesheets
		.filter((t) => date.isSame(t.localDate, 'day'))
		.toArray()
	return timesheetsInCache.length > 0
}

/**
 * Synchronize local changes with the backend
 */
export const syncWithBackend = async (db: ForemanDB): Promise<void> => {
	const currentSyncState = await db.syncState.get(1)
	if (currentSyncState && currentSyncState.syncInProgress === true) {
		return
	}

	try {
		await db.setSyncInProgress(true)

		const { id } = useTokenClaims()
		if (!id) {
			throw new Error('User ID not found in token claims')
		}

		// Initialize snapshot if needed
		const mutationSnapshotService = new MutationSnapshotService(db)
		await mutationSnapshotService.initializeSnapshot(id)

		// Get changes for sync
		const snapshot = await mutationSnapshotService.getSnapshotForSync()
		if (!snapshot) {
			// No changes to sync
			return
		}
		const logs = await db.mutations.toArray()
		// Send to backend
		const res = await sendSnapshotToBackend(snapshot, logs)

		if (res) {
			// if(typeof res === 'object' && res.data.length > 0) {
			// 	const updatedAttachments = await db.getAttachmentsXFDF()
			// 	// Process attachment annotations if there are updated attachments and upload responses
			// 	if (updatedAttachments && updatedAttachments.length > 0) {
			// 		try {
			// 			// Extract the entity from the change tracker
			// 			const attachmentEntities = updatedAttachments.map(change => change.entity)
			// 			await processAttachmentAnnotations(attachmentEntities, res.data, db)
			// 		} catch (error) {
			// 			console.error('Error processing attachment annotations:', error)
			// 			// Continue with sync even if attachment processing fails
			// 		}
			// 	}
			// }
			// Mark as synced
			await mutationSnapshotService.markAsSynced()
			await db.mutations.clear()

		}
	} catch (error) {
		console.error('Error during sync:', error)
	} finally {
		await db.setSyncInProgress(false)
	}
}

/**
 * Convert Date objects to local timezone strings to prevent UTC conversion
 */
const convertDatesToLocalStrings = (obj: any): any => {
	if (obj === null || obj === undefined) return obj
	if (obj instanceof Date) {
		const pad = (n: number, len = 2) => n.toString().padStart(len, '0')
		return `${obj.getFullYear()}-${pad(obj.getMonth() + 1)}-${pad(obj.getDate())}T${pad(
			obj.getHours(),
		)}:${pad(obj.getMinutes())}:${pad(obj.getSeconds())}.${pad(obj.getMilliseconds(), 3)}`
	}
	if (Array.isArray(obj)) return obj.map(convertDatesToLocalStrings)
	if (typeof obj === 'object')
		return Object.fromEntries(
			Object.entries(obj).map(([k, v]) => [k, convertDatesToLocalStrings(v)]),
		)
	return obj
}

/**
 * Send the mutation snapshot to the backend
 */
const sendSnapshotToBackend = async (snapshot: MutationSnapshot, logs: OfflineMutation[]) => {
	try {
		const { projectId } = useTokenClaims()
		if (!projectId) {
			throw new Error('Project ID not found in token claims')
		}

		// Prepare the payload for the backend
		const syncPayload = prepareSyncPayload(snapshot, logs)

		// if all changes are unchanged, return true
		if (
			Object.values(syncPayload.changes).every(
				(c: any) => c.updated.length === 0 && c.inserted.length === 0 && c.deleted.length === 0,
			)
		) {
			return true
		}

		// Send to backend endpoint
		const response = await postWithResponse<AttachmentsUploadResponse[]>(
			`/Offline/Sync`,
			syncPayload,
		)
		return response
	} catch (error) {
		return false
	}
}

/**
 * Prepare the sync payload for the backend
 * This transforms the MutationSnapshot into a format the backend expects
 */
const prepareSyncPayload = (snapshot: MutationSnapshot, logs: OfflineMutation[]): any => {
	const payload: any = {
		userId: snapshot.userId,
		timestamp: new Date(),
		//snapshot.createdAt,
		changes: {},
		logs,
	}

	// Process each entity type
	const entityTypes = [
		'directReports',
		'workItems',
		'tasks',
		'reworks',
		'constraints',
		'requests',
		'workItemAssignments',
		'taskAssignments',
		'reworkAssignments',
		'qcWorkers',
		'attendance',
		'pallets',
		'palletItems',
		'timesheets',
		'timeRecords',
		'progressRecords',
		'attachments',
		'updatedAttachments',
	] as const

	entityTypes.forEach((entityType) => {
		const changes = snapshot[entityType]
		if (Array.isArray(changes) && changes.length > 0) {
			const typedChanges = changes as Array<{
				changeType: string
				entity: any
				originalId?: any
				uid: string
				logId: string
			}>
			if (entityType === 'updatedAttachments') {
				payload.changes[entityType] = {
					updated: typedChanges
						.filter((c) => c.changeType === 'updated')
						.map((c) => ({
							item: { id: c.entity.attachmentId, XFDFAnnotations: c.entity.newAnnotations },
							mutationId: c.uid,
							logId: c.logId,
						})),
				}
			} else {
				payload.changes[entityType] = {
					inserted: typedChanges
						.filter((c) => c.changeType === 'inserted')
						.map((c) => ({
							item: c.entity,
							mutationId: c.uid,
							logId: c.logId,
						})),
					updated: typedChanges
						.filter((c) => c.changeType === 'updated')
						.map((c) => ({
							item: {
								...c.entity,
								originalId: c.originalId,
							},
							mutationId: c.uid,
							logId: c.logId,
						})),
					deleted: typedChanges
						.filter((c) => c.changeType === 'deleted')
						.map((c) => ({
							item: c.entity,
							mutationId: c.uid,
							logId: c.logId,
						})),
				}
			}
		}
	})

	// Handle single entities
	if (snapshot.employee) {
		payload.changes.employee = {
			[snapshot.employee.changeType]: {
				item: snapshot.employee.entity,
				mutationId: snapshot.employee.uid,
				logId: snapshot.employee.logId,
			},
		}
	}

	// Convert all Date objects to local timezone strings to prevent UTC conversion
	return convertDatesToLocalStrings(payload)
}

/**
 * Get sync status
 */
export const getSyncStatus = async (
	db: ForemanDB,
): Promise<{
	hasChanges: boolean
}> => {
	try {
		const { id } = useTokenClaims()
		if (!id) {
			return { hasChanges: false }
		}

		const mutationSnapshotService = new MutationSnapshotService(db)
		await mutationSnapshotService.initializeSnapshot(id)
		const snapshot = await mutationSnapshotService.getSnapshotForSync()

		return {
			hasChanges: snapshot !== null,
		}
	} catch (error) {
		console.error('Error getting sync status:', error)
		return { hasChanges: false }
	}
}
